import React, { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
    User, Send, Zap, Calendar, ClipboardList,
    MoreHorizontal, ArrowRight, PauseCircle, PlayCircle,
    Tag, Activity, History, ShieldCheck, Mail, AlertCircle,
    ChevronDown, Clock, MousePointer2, ExternalLink
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import RichReplyComposer from './RichReplyComposer';
import { api } from '../api/api';
import { cn } from '../lib/utils';
import { Card, CardContent } from './ui/card';

// Email quote divider patterns (Turkish, English, Gmail, Outlook, Apple Mail)
const QUOTE_PATTERNS = [
    /^(?:[-_=\s]{2,})?\s*Gönderen\s*:/i,
    /^(?:[-_=\s]{2,})?\s*From\s*:/i,
    /^On .+ (?:wrote|yazdı)\s*:/i,
    /^Kimden\s*:/i,
    /^-+\s*Original Message\s*-+/i,
    /^-+\s*Orijinal Mesaj\s*-+/i,
    /^Begin forwarded message\s*:/i
];

function splitEmailBody(rawContent) {
    if (!rawContent) return { mainText: '', quotedText: '' };

    // 1. Clean cid attachments like [cid:43d1122f...]
    const cleaned = rawContent
        .replace(/\[cid:[^\]]+\]/gi, '')
        .replace(/<mailto:[^>]+>/gi, '');

    const lines = cleaned.split(/\r?\n/);
    let splitIndex = -1;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (QUOTE_PATTERNS.some(pattern => pattern.test(line))) {
            splitIndex = i;
            break;
        }
    }

    if (splitIndex !== -1) {
        return {
            mainText: lines.slice(0, splitIndex).join('\n').trim(),
            quotedText: lines.slice(splitIndex).join('\n').trim()
        };
    }

    return { mainText: cleaned.trim(), quotedText: '' };
}

// Simple resilient Markdown formatter for email thread bodies
function MarkdownMessage({ content }) {
    const [showQuote, setShowQuote] = useState(false);

    if (!content) {
        return <span className="italic opacity-50 font-bold uppercase tracking-widest text-[10px]">(Null Body Transmitted)</span>;
    }

    const { mainText, quotedText } = splitEmailBody(content);

    const renderMarkdownContent = (text) => {
        const lines = text.split(/\r?\n/);
        const elements = [];
        let currentParagraph = [];

        const flushParagraph = (key) => {
            if (currentParagraph.length > 0) {
                elements.push(
                    <p key={key} className="mb-2 leading-relaxed break-words whitespace-pre-wrap">
                        {renderInline(currentParagraph.join('\n'))}
                    </p>
                );
                currentParagraph = [];
            }
        };

        const renderInline = (inlineText) => {
            const tokens = [];
            // Match urls, markdown links [title](url), **bold**, *italic*, `code`
            const regex = /(\[([^\]]+)\]\(([^)]+)\))|(https?:\/\/[^\s<>]+)|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)/g;
            let lastIndex = 0;
            let match;

            while ((match = regex.exec(inlineText)) !== null) {
                if (match.index > lastIndex) {
                    tokens.push(inlineText.substring(lastIndex, match.index));
                }
                if (match[1]) {
                    // [title](url)
                    tokens.push(
                        <a key={match.index} href={match[3]} target="_blank" rel="noopener noreferrer" className="text-primary underline font-medium hover:text-primary/80 break-all">
                            {match[2]}
                        </a>
                    );
                } else if (match[4]) {
                    // bare url
                    tokens.push(
                        <a key={match.index} href={match[4]} target="_blank" rel="noopener noreferrer" className="text-primary underline font-medium hover:text-primary/80 break-all">
                            {match[4]}
                        </a>
                    );
                } else if (match[5]) {
                    // **bold**
                    tokens.push(<strong key={match.index} className="font-bold text-foreground">{match[6]}</strong>);
                } else if (match[7]) {
                    // *italic*
                    tokens.push(<em key={match.index} className="italic">{match[8]}</em>);
                } else if (match[9]) {
                    // `code`
                    tokens.push(<code key={match.index} className="px-1.5 py-0.5 rounded bg-muted text-foreground font-mono text-xs border border-border/40">{match[10]}</code>);
                }
                lastIndex = regex.lastIndex;
            }

            if (lastIndex < inlineText.length) {
                tokens.push(inlineText.substring(lastIndex));
            }

            return tokens.length > 0 ? tokens : inlineText;
        };

        lines.forEach((line, idx) => {
            const trimmed = line.trim();
            if (trimmed.startsWith('>')) {
                flushParagraph(`p-${idx}`);
                elements.push(
                    <blockquote key={`quote-${idx}`} className="border-l-2 border-primary/40 pl-3 my-2 text-muted-foreground italic text-xs break-words">
                        {renderInline(trimmed.replace(/^>\s*/, ''))}
                    </blockquote>
                );
            } else if (trimmed.startsWith('---') || trimmed.startsWith('___') || trimmed.startsWith('===')) {
                flushParagraph(`p-${idx}`);
                elements.push(<hr key={`hr-${idx}`} className="my-3 border-border/60" />);
            } else if (trimmed.startsWith('# ')) {
                flushParagraph(`p-${idx}`);
                elements.push(<h3 key={`h1-${idx}`} className="text-base font-bold text-foreground mt-3 mb-1">{renderInline(trimmed.substring(2))}</h3>);
            } else if (trimmed.startsWith('## ')) {
                flushParagraph(`p-${idx}`);
                elements.push(<h4 key={`h2-${idx}`} className="text-sm font-bold text-foreground mt-2 mb-1">{renderInline(trimmed.substring(3))}</h4>);
            } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
                flushParagraph(`p-${idx}`);
                elements.push(
                    <li key={`li-${idx}`} className="ml-4 list-disc text-foreground leading-relaxed">
                        {renderInline(trimmed.substring(2))}
                    </li>
                );
            } else if (/^\d+\.\s/.test(trimmed)) {
                flushParagraph(`p-${idx}`);
                const textAfterNum = trimmed.replace(/^\d+\.\s*/, '');
                elements.push(
                    <li key={`oli-${idx}`} className="ml-4 list-decimal text-foreground leading-relaxed">
                        {renderInline(textAfterNum)}
                    </li>
                );
            } else if (!trimmed) {
                flushParagraph(`p-${idx}`);
            } else {
                currentParagraph.push(line);
            }
        });

        flushParagraph(`p-final`);
        return elements;
    };

    return (
        <div className="markdown-body space-y-1 text-inherit">
            {/* Primary message text from sender */}
            {renderMarkdownContent(mainText || content)}

            {/* Quoted / Replied previous email history */}
            {quotedText && (
                <div className="mt-4 pt-3 border-t border-border/40">
                    <button
                        type="button"
                        onClick={() => setShowQuote(!showQuote)}
                        className="flex items-center gap-1.5 text-xs font-bold text-muted-foreground hover:text-foreground bg-muted/30 hover:bg-muted/60 px-3 py-1.5 rounded-xl border border-border/40 transition-colors"
                    >
                        <ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-200", showQuote && "rotate-180")} />
                        <span>{showQuote ? 'Alıntılanan Orijinal Mesajı Gizle' : 'Alıntılanan Orijinal Mesajı Göster (...)'}</span>
                    </button>

                    {showQuote && (
                        <div className="mt-3 p-4 rounded-xl bg-background/60 border border-border/40 text-xs text-muted-foreground opacity-90 transition-all animate-in fade-in duration-300">
                            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70 mb-2">
                                Önceki İletişim Geçmişi (Quoted Thread)
                            </div>
                            {renderMarkdownContent(quotedText)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function ThreadDetail({ threadData, onReplySent, onActionCompleted }) {
    const { messages, context, threadId } = threadData;
    const [replying, setReplying] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [newTag, setNewTag] = useState('');
    const [members, setMembers] = useState([]);

    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const res = await api.get('/api/workspace/members');
                setMembers(res || []);
            } catch (err) {
                console.error('Failed to fetch members', err);
            }
        };
        fetchMembers();
    }, []);

    const handleSendReply = async ({ body, stopAutomations }) => {
        setActionLoading(true);
        try {
            const latestInbound = [...messages].reverse().find(m => m.type === 'INBOUND');
            if (!latestInbound) throw new Error("No inbound message found to reply to.");

            await api.post(`/api/inbox/${latestInbound._id}/reply`, { body, stopAutomations });
            onReplySent();
            setReplying(false);
        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleCrmUpdate = async (updates) => {
        if (!context?.contact) return;
        setActionLoading(true);
        try {
            await api.put(`/api/contacts/${context.contact._id}`, updates);
            onActionCompleted();
        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleCreateTask = async () => {
        if (!context?.contact) return;
        setActionLoading(true);
        try {
            await api.post('/api/tasks', {
                title: `Follow up: ${threadData.subject || messages[0]?.subject}`,
                linkedContactId: context.contact._id,
                priority: 'MEDIUM',
                notes: `Action required on thread ${threadId}`
            });
            onActionCompleted();
        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleAddTag = async () => {
        if (!newTag.trim() || !context?.contact) return;
        const tags = [...(context.contact.tags || []), newTag.trim()];
        handleCrmUpdate({ tags });
        setNewTag('');
    };

    const handleMarkHandled = async () => {
        setActionLoading(true);
        try {
            const latestInbound = [...messages].reverse().find(m => m.type === 'INBOUND');
            if (latestInbound) {
                await api.patch(`/api/inbox/${latestInbound._id}/handled`, { isHandled: true });
            }
            onActionCompleted();
        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleTogglePause = async () => {
        if (!context?.contact) return;
        setActionLoading(true);
        try {
            const nextState = !context.contact.isPaused;
            await api.patch(`/api/inbox/contact/${context.contact._id}/pause`, { isPaused: nextState });
            onActionCompleted();
        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleResolveTask = async (taskId, action) => {
        setActionLoading(true);
        try {
            await api.post(`/api/tasks/${taskId}/${action}`);
            onActionCompleted();
        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleMeetingState = async (state) => {
        if (!context?.contact) return;
        setActionLoading(true);
        try {
            await api.put(`/api/contacts/${context.contact._id}/meeting-state`, { meetingState: state });
            onActionCompleted();
        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-background overflow-hidden">
            {/* Header: Identity & Context */}
            <div className="px-8 py-5 border-b bg-card/10 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-5">
                    <div className="h-12 w-12 rounded-2xl bg-primary/10 border-2 border-primary/20 flex items-center justify-center text-primary shadow-sm hover:scale-105 transition-transform cursor-pointer">
                        <User className="h-7 w-7" />
                    </div>
                    <div>
                        <div className="flex items-center gap-3">
                            <h2 className="text-xl font-black tracking-tight text-foreground truncate max-w-[300px]">
                                {context?.contact ? `${context.contact.firstName} ${context.contact.lastName}` : (messages[0]?.from || 'Unknown Participant')}
                            </h2>
                            {context?.contact?.isPaused && (
                                <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20 font-black text-[9px] uppercase tracking-widest px-2.5 py-0.5 animate-pulse">
                                    <PauseCircle className="w-3 h-3 mr-1" /> Automation Paused
                                </Badge>
                            )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-60">
                                {threadData.subject || messages[0]?.subject || 'Direct Outreach Thread'}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {context?.sequence && (
                        <div className="hidden sm:flex items-center gap-2 bg-blue-500/5 px-4 py-2 rounded-2xl border-2 border-dashed border-blue-500/20">
                            <Zap className="w-4 h-4 text-blue-500" />
                            <div className="flex flex-col text-left">
                                <span className="text-[9px] font-black text-blue-600/60 uppercase tracking-widest leading-none">Active Sequence</span>
                                <span className="text-[11px] font-black text-blue-600 truncate max-w-[150px] leading-tight mt-0.5">{context.sequence.name}</span>
                            </div>
                        </div>
                    )}
                    <Button variant="outline" size="icon" className="h-11 w-11 rounded-2xl shadow-sm hover:shadow-md transition-all">
                        <MoreHorizontal className="w-5 h-5" />
                    </Button>
                </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
                {/* Main conversation area */}
                <div className="flex-1 flex flex-col min-w-0 h-full">
                    <ScrollArea className="flex-1 px-8">
                        <div className="space-y-10 py-10 max-w-5xl mx-auto">
                            {messages.map((msg, idx) => {
                                const isOutbound = msg.type === 'OUTBOUND';
                                return (
                                    <div key={msg._id} className={cn(
                                        "flex flex-col gap-3 group animate-in fade-in slide-in-from-bottom-2 duration-500",
                                        isOutbound ? "ml-auto items-end text-right" : "items-start text-left"
                                    )}>
                                        <div className="flex items-center gap-3 mb-1 px-1">
                                            <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest opacity-60 group-hover:opacity-100 transition-opacity">
                                                {isOutbound ? 'Internal Orchestrator' : (msg.from || 'Participant')}
                                                {!isOutbound && (msg.mailAccountId?.user || msg.to) && (
                                                    <span className="ml-1 text-primary/80 font-bold">
                                                        → {msg.mailAccountId?.user || msg.to}
                                                    </span>
                                                )}
                                                {' '}• {formatDistanceToNow(new Date(msg.receivedAt), { addSuffix: true })}
                                            </span>
                                        </div>
                                        
                                        <div className={cn(
                                            "relative group/bubble",
                                            isOutbound ? "items-end" : "items-start"
                                        )}>
                                            <div className={cn(
                                                "p-6 rounded-[2rem] text-sm leading-relaxed shadow-lg border-2 transition-all group-hover/bubble:shadow-xl max-w-3xl",
                                                isOutbound 
                                                    ? "bg-primary text-primary-foreground rounded-tr-none border-primary/20" 
                                                    : "bg-card text-card-foreground dark:bg-muted/40 dark:text-foreground rounded-tl-none border-border/80 dark:border-border/40"
                                            )}>
                                                <MarkdownMessage content={msg.body} />
                                            </div>
                                            
                                            {/* Indicators */}
                                            <div className={cn(
                                                "absolute -bottom-6 flex items-center gap-2",
                                                isOutbound ? "right-2" : "left-2"
                                            )}>
                                                {msg.classification && msg.classification !== 'REPLY' && (
                                                    <Badge variant="outline" className="text-[9px] uppercase font-black tracking-[0.15em] bg-accent/5 border-border/40 px-2 py-0.5 shadow-sm">
                                                        <Tag className="w-2.5 h-2.5 mr-1 text-primary" /> {msg.classification}
                                                    </Badge>
                                                )}
                                                {isOutbound && (
                                                    <Badge variant="outline" className="text-[8px] uppercase font-black tracking-widest bg-emerald-500/5 text-emerald-600 border-emerald-500/20 px-2 shadow-sm">
                                                        <Mail className="w-2.5 h-2.5 mr-1" /> Delivered
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>

                                        {/* Automation Activity Trace - Refined */}
                                        {msg.automationRuns && msg.automationRuns.length > 0 && (
                                            <div className="mt-8 flex flex-col gap-2 w-full">
                                                <div className="flex items-center gap-2 opacity-50">
                                                    <div className="h-px flex-1 bg-border/60" />
                                                    <div className="flex items-center gap-2 px-3 py-1 rounded-full border bg-muted/5">
                                                        <Activity className="h-3 w-3 text-primary" />
                                                        <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground whitespace-nowrap">Execution Node Trace</span>
                                                    </div>
                                                    <div className="h-px flex-1 bg-border/60" />
                                                </div>
                                                <div className={cn(
                                                    "flex flex-wrap gap-2",
                                                    isOutbound ? "justify-end" : "justify-start"
                                                )}>
                                                    {msg.automationRuns.map((run, rIdx) => (
                                                        <Card key={rIdx} className="bg-muted/5 border-2 border-border/40 shadow-none hover:shadow-sm hover:border-primary/20 transition-all cursor-default overflow-hidden group/trace">
                                                            <CardContent className="p-2.5 flex items-center gap-3">
                                                                <div className={cn(
                                                                    "w-7 h-7 rounded-lg flex items-center justify-center transition-colors shadow-inner",
                                                                    run.status === 'COMPLETED' ? "bg-emerald-500/10 text-emerald-600 group-hover/trace:bg-emerald-500 group-hover/trace:text-white" : "bg-amber-500/10 text-amber-600 group-hover/trace:bg-amber-500 group-hover/trace:text-white"
                                                                )}>
                                                                    <Zap className="h-3.5 w-3.5" />
                                                                </div>
                                                                <div className="flex flex-col leading-tight pr-1">
                                                                    <span className="text-[10px] font-black uppercase tracking-tight text-foreground">{run.automationType === 'LEGACY_RULE' ? 'Native Rule' : 'Protocol Node'}</span>
                                                                    <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-widest opacity-60">Status: {run.status.toLowerCase()}</span>
                                                                </div>
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </ScrollArea>

                    {/* Composer Area */}
                    <div className="p-8 border-t bg-card/5 backdrop-blur-sm">
                        <div className="max-w-5xl mx-auto w-full">
                            {replying ? (
                                <RichReplyComposer 
                                    loading={actionLoading} 
                                    onSend={handleSendReply} 
                                    recipient={messages[0]?.from}
                                    threadSubject={threadData.subject || messages[0]?.subject}
                                />
                            ) : (
                                <div className="flex gap-4">
                                    <Button 
                                        className="flex-1 h-14 rounded-2xl gap-3 font-black uppercase tracking-[0.15em] text-[10px] shadow-xl shadow-primary/10 border-2"
                                        variant="outline"
                                        onClick={() => setReplying(true)}
                                    >
                                        <Send className="h-4 w-4" /> Compose manual intervention...
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-14 w-14 rounded-2xl border-2 hover:bg-white transition-all active:scale-95">
                                        <MoreHorizontal className="h-5 w-5" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sidebar Context */}
                <div className="w-[340px] border-l bg-muted/5 hidden lg:flex flex-col p-8 space-y-8 overflow-y-auto custom-scrollbar shadow-inner">
                    {/* Urgency / Pending Task */}
                    {context?.pendingTask && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-700">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-rose-500 flex items-center gap-2">
                                <AlertCircle className="w-3.5 h-3.5" /> Intelligence Alert
                            </h4>
                            <Card className="border-2 border-rose-500/20 bg-rose-500/5 shadow-xl relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-3 opacity-5 pointer-events-none group-hover:opacity-10 transition-opacity">
                                    <Zap className="w-20 h-20" />
                                </div>
                                <CardContent className="p-6">
                                    <h5 className="font-black text-sm text-foreground mb-1.5 leading-tight">{context.pendingTask.title}</h5>
                                    <p className="text-[11px] font-medium text-muted-foreground mb-5 leading-relaxed opacity-80">{context.pendingTask.description || 'System requested human mediation for this protocol branch.'}</p>
                                    <div className="grid grid-cols-2 gap-3 relative z-10">
                                        <Button size="sm" onClick={() => handleResolveTask(context.pendingTask._id, 'complete')} disabled={actionLoading} className="bg-rose-600 hover:bg-rose-500 text-white font-black text-[9px] uppercase tracking-widest h-10 rounded-xl shadow-lg shadow-rose-500/10">Approve</Button>
                                        <Button size="sm" variant="outline" onClick={() => handleResolveTask(context.pendingTask._id, 'skip')} disabled={actionLoading} className="border-rose-500/20 text-rose-600 hover:bg-rose-50 font-black text-[9px] uppercase tracking-widest h-10 rounded-xl">Discard</Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                    
                    {/* Profile & CRM */}
                    <div className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.25em] text-muted-foreground opacity-50">Profile Architecture</h4>
                        {context?.contact ? (
                            <div className="p-6 rounded-[2rem] border-2 bg-white shadow-sm space-y-5 hover:shadow-md transition-all">
                                <div className="flex items-start gap-4">
                                    <div className="h-12 w-12 rounded-2xl bg-primary/5 border-2 border-primary/10 flex items-center justify-center shrink-0 shadow-inner">
                                        <User className="h-6 w-6 text-primary" />
                                    </div>
                                    <div className="flex flex-col min-w-0 pr-2">
                                        <span className="text-sm font-black text-foreground truncate">{context.contact.firstName} {context.contact.lastName}</span>
                                        <span className="text-[10px] font-bold text-muted-foreground truncate opacity-60 underline decoration-primary/20">{context.contact.personMasterId?.email || messages[0]?.from}</span>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    <Badge variant="secondary" className="text-[9px] uppercase font-black px-2.5 py-0.5 border-2 border-transparent bg-muted/30">Stage: {context.contact.stage}</Badge>
                                    <Badge variant="outline" className="text-[9px] uppercase font-black px-2.5 py-0.5 border-2 border-border/40">Status: {context.contact.status}</Badge>
                                </div>

                                <div className="pt-5 border-t-2 border-dashed border-border/40 space-y-3">
                                    <h5 className="text-[9px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                        <Calendar className="w-3 h-3 text-primary" /> Synchronized Ops
                                    </h5>
                                    <div className="relative group">
                                        <select 
                                            className="w-full text-xs font-black bg-muted/5 border-2 border-border/40 rounded-xl p-3 pr-10 appearance-none focus:border-primary outline-none transition-all shadow-inner"
                                            value={context.contact.meetingState || 'NONE'}
                                            onChange={(e) => handleMeetingState(e.target.value)}
                                            disabled={actionLoading}
                                        >
                                            <option value="NONE">Protocol: Standard</option>
                                            <option value="BOOKED">Upcoming Engagement</option>
                                            <option value="COMPLETED">Lifecycle: Success</option>
                                            <option value="NO_SHOW">Lifecycle: Gap</option>
                                            <option value="CANCELLED">Lifecycle: Aborted</option>
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none group-hover:text-primary transition-colors" />
                                    </div>
                                    {context.contact.meetingState === 'BOOKED' && (
                                        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/5 rounded-xl border-2 border-emerald-500/20 animate-pulse">
                                            <Clock className="w-3 h-3 text-emerald-600" />
                                            <span className="text-[9px] text-emerald-600 font-black uppercase tracking-widest leading-none">High-Intent Scheduled</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="p-8 rounded-[2rem] border-2 border-dashed border-border/60 text-center space-y-3 bg-muted/10 opacity-60">
                                <p className="text-[10px] font-black uppercase tracking-widest leading-relaxed">No CRM records identified for this participant.</p>
                                <Button variant="link" size="sm" className="h-auto p-0 text-[10px] font-black uppercase tracking-widest text-primary">+ Create Lead Intelligence</Button>
                            </div>
                        )}
                    </div>

                    {/* Sequence & Execution Trace */}
                    <div className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.25em] text-muted-foreground opacity-50">{context?.sequence ? "Source Intelligence" : "Origin Protocol"}</h4>
                        {context?.sequence ? (
                            <div className="p-6 rounded-[2rem] border-2 bg-blue-500/5 border-blue-500/10 shadow-sm space-y-5">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center text-white shadow-lg">
                                        <Zap className="h-5 w-5" />
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-xs font-black uppercase tracking-tight truncate">{context.sequence.name}</span>
                                        <span className="text-[9px] text-blue-600/60 font-black uppercase tracking-widest mt-0.5">Status: {context.sequence.status}</span>
                                    </div>
                                </div>
                                {context.assignment && (
                                    <div className="space-y-4 pt-5 border-t-2 border-blue-500/10">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground opacity-50">Local Status</span>
                                                <span className="text-[11px] font-black text-foreground">{context.assignment.status}</span>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground opacity-50">Active Phase</span>
                                                <span className="text-[11px] font-black text-foreground">Step {context.assignment.currentStepNumber}</span>
                                            </div>
                                        </div>
                                        
                                        {context.assignment.nextStepScheduledAt && (
                                            <div className="p-3 bg-blue-500/10 rounded-2xl border-2 border-blue-500/20 flex flex-col gap-1 shadow-inner">
                                                <span className="text-[9px] font-black uppercase tracking-widest text-blue-600/70">Next Orchestration Due</span>
                                                <span className="text-xs font-black text-blue-700">{formatDistanceToNow(new Date(context.assignment.nextStepScheduledAt), { addSuffix: true })}</span>
                                            </div>
                                        )}

                                        {context.assignment.history && context.assignment.history.length > 0 && (
                                            <div className="pt-5 border-t-2 border-blue-500/10 space-y-5">
                                                <h5 className="text-[9px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2 opacity-60">
                                                    <History className="w-3.5 h-3.5" /> High-Density Trace
                                                </h5>
                                                <div className="relative pl-3 space-y-6 before:absolute before:left-[1px] before:top-1 before:bottom-1 before:w-0.5 before:bg-blue-500/20">
                                                    {context.assignment.history.slice(-4).reverse().map((hist, hIdx) => (
                                                        <div key={hIdx} className="relative group/h">
                                                            <div className="absolute -left-3.5 w-2 h-2 rounded-full border-2 border-blue-500 bg-white shadow-sm z-10 scale-75 group-hover/h:scale-100 transition-transform" />
                                                            <div className="space-y-1">
                                                                <div className="flex justify-between items-start gap-2">
                                                                    <span className="text-[10px] font-black uppercase tracking-tight text-foreground truncate">{hist.actionType.replace(/_/g, ' ')}</span>
                                                                    <span className="text-[8px] font-mono opacity-40 whitespace-nowrap">{new Date(hist.executedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                                </div>
                                                                <p className="text-[9px] font-bold text-muted-foreground leading-normal opacity-70 group-hover:opacity-100 transition-opacity">
                                                                    {hist.status} • {hist.details?.targetNodeId || 'Node ' + (hist.stepNumber || 'root')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="p-6 rounded-[2rem] border-2 bg-muted/5 border-border/40 text-center grayscale opacity-60 italic text-[10px] font-black uppercase tracking-widest">
                                Protocol: Manual Engagement
                            </div>
                        )}
                    </div>

                    <Separator className="opacity-50" />

                    {/* Operational Actions */}
                    <div className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.25em] text-muted-foreground opacity-50">Local Operations</h4>
                        <div className="grid grid-cols-1 gap-2.5">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                className={cn(
                                    "justify-start gap-4 h-12 text-[10px] font-black uppercase tracking-widest rounded-2xl border-2 transition-all active:scale-[0.98]",
                                    context?.contact?.isPaused ? "border-emerald-500/20 text-emerald-600 hover:bg-emerald-50" : "border-amber-500/20 text-amber-600 hover:bg-amber-50"
                                )}
                                onClick={handleTogglePause}
                                disabled={actionLoading || !context?.contact}
                            >
                                {context?.contact?.isPaused ? <PlayCircle className="h-4 w-4" /> : <PauseCircle className="h-4 w-4" />}
                                {context?.contact?.isPaused ? "Resume Human Protocol" : "Suspend Orchestration"}
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="justify-start gap-4 h-12 text-[10px] font-black uppercase tracking-widest rounded-2xl border-2 hover:bg-white active:scale-[0.98]"
                                onClick={handleMarkHandled}
                                disabled={actionLoading}
                            >
                                <ShieldCheck className="h-4 w-4 text-emerald-500" /> Archive Objective
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="justify-start gap-4 h-12 text-[10px] font-black uppercase tracking-widest rounded-2xl border-2 hover:bg-white active:scale-[0.98]"
                                onClick={handleCreateTask}
                                disabled={actionLoading || !context?.contact}
                            >
                                <ClipboardList className="h-4 w-4 text-blue-500" /> Delegate Intervention
                            </Button>
                        </div>
                    </div>

                    <Separator className="opacity-50" />

                    {/* CRM Attributes Refined */}
                    <div className="space-y-6 pb-10">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.25em] text-muted-foreground opacity-50">Data Integration</h4>
                        {context?.contact && (
                            <div className="space-y-6">
                                <div className="space-y-2.5">
                                    <label className="text-[9px] font-black uppercase tracking-widest opacity-60 ml-3 flex items-center gap-2">
                                        <MousePointer2 className="w-3 h-3" /> Assignee
                                    </label>
                                    <Select 
                                        value={context.contact.ownerId || 'unassigned'} 
                                        onValueChange={(v) => handleCrmUpdate({ ownerId: v === 'unassigned' ? null : v })}
                                    >
                                        <SelectTrigger className="h-12 text-xs font-black uppercase tracking-tight rounded-2xl border-2 bg-white/50 backdrop-blur-sm">
                                            <SelectValue placeholder="Unassigned Intelligence" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="unassigned" className="text-[10px] font-black uppercase tracking-widest">Unassigned Intelligence</SelectItem>
                                            {members.map(m => (
                                                <SelectItem key={m.userId} value={m.userId} className="text-[10px] font-black uppercase tracking-widest">{m.name || m.email}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2.5">
                                    <label className="text-[9px] font-black uppercase tracking-widest opacity-60 ml-3 flex items-center gap-2">
                                        <ExternalLink className="w-3 h-3" /> Engagement Phase
                                    </label>
                                    <Select value={context.contact.stage} onValueChange={(v) => handleCrmUpdate({ stage: v })}>
                                        <SelectTrigger className="h-12 text-xs font-black uppercase tracking-tight rounded-2xl border-2 bg-white/50 backdrop-blur-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="LEAD" className="text-[10px] font-black uppercase tracking-widest">Discovery Phase</SelectItem>
                                            <SelectItem value="QUALIFIED" className="text-[10px] font-black uppercase tracking-widest">Validated Intelligence</SelectItem>
                                            <SelectItem value="OPPORTUNITY" className="text-[10px] font-black uppercase tracking-widest">Strategic Pipeline</SelectItem>
                                            <SelectItem value="CUSTOMER" className="text-[10px] font-black uppercase tracking-widest">Acquisition Complete</SelectItem>
                                            <SelectItem value="LOST" className="text-[10px] font-black uppercase tracking-widest">Archived Lead</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-3">
                                    <label className="text-[9px] font-black uppercase tracking-widest opacity-60 ml-3 flex items-center gap-2">
                                        <Tag className="w-3 h-3" /> Intelligence Tags
                                    </label>
                                    <div className="flex flex-wrap gap-1.5 px-3">
                                        {context.contact.tags?.map(tag => (
                                            <Badge key={tag} variant="secondary" className="text-[9px] font-black uppercase tracking-widest bg-primary/5 text-primary border-primary/10 px-2.5 shadow-sm">{tag}</Badge>
                                        ))}
                                    </div>
                                    <div className="flex gap-2 relative group mt-2">
                                        <Input 
                                            value={newTag} 
                                            onChange={e => setNewTag(e.target.value)}
                                            placeholder="Append Metadata..."
                                            className="h-11 text-[10px] font-black uppercase tracking-widest rounded-2xl border-2 shadow-inner pl-4 bg-white/50"
                                        />
                                        <Button size="icon" variant="outline" className="h-11 w-11 shrink-0 rounded-2xl border-2 active:scale-95 transition-all" onClick={handleAddTag} disabled={!newTag}>
                                            <ArrowRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
